/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function getSpeechRecognition() {
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

function initSpeechSearchButton(button) {
  if (!button || button.dataset.speechInit === 'true') return;
  button.dataset.speechInit = 'true';

  const predictiveSearch = button.closest('predictive-search');
  const SpeechRecognition = getSpeechRecognition();

  if (!SpeechRecognition) {
    if (predictiveSearch) predictiveSearch.classList.add('speech-search--failed');
    button.hidden = true;
    return;
  }

  const form = button.closest('form');
  const searchInput = form && form.querySelector('.js-search-input');

  if (!form || !searchInput) {
    if (predictiveSearch) predictiveSearch.classList.add('speech-search--failed');
    button.hidden = true;
    return;
  }

  let recognition;
  try {
    recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
  } catch (error) {
    if (predictiveSearch) predictiveSearch.classList.add('speech-search--failed');
    button.hidden = true;
    return;
  }

  let isListening = false;

  recognition.addEventListener(
    'result',
    debounce((evt) => {
      if (!evt.results || !evt.results.length) return;

      const term = evt.results[0][0].transcript;
      searchInput.value = term;

      if (searchInput.getAttribute('role') === 'combobox') {
        searchInput.dispatchEvent(new Event('input', { bubbles: true }));
      } else {
        form.submit();
      }
    }, 300)
  );

  recognition.addEventListener('audiostart', () => {
    isListening = true;
    button.classList.add('search__speech-listening');
  });

  recognition.addEventListener('audioend', () => {
    isListening = false;
    button.classList.remove('search__speech-listening');
  });

  button.addEventListener('click', (evt) => {
    evt.preventDefault();
    if (isListening) {
      recognition.stop();
    } else {
      try {
        recognition.start();
      } catch (error) {
        // Ignore InvalidStateError when start() is called too quickly after stop().
      }
    }
  });

  button.addEventListener('keydown', (evt) => {
    if (evt.code === 'Space' || evt.code === 'Enter') {
      evt.preventDefault();
      button.click();
    }
  });

  button.hidden = false;
  if (predictiveSearch) predictiveSearch.classList.add('speech-search--enabled');
}

function initAllSpeechSearchButtons() {
  document.querySelectorAll('speech-search-button').forEach(initSpeechSearchButton);
}

if (!customElements.get('speech-search-button')) {
  class SpeechSearchButton extends HTMLElement {
    connectedCallback() {
      initSpeechSearchButton(this);
    }
  }

  customElements.define('speech-search-button', SpeechSearchButton);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllSpeechSearchButtons);
} else {
  initAllSpeechSearchButtons();
}
